import { Button, Icon, Pill } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import { useCallSession } from './CallSession.tsx';

const DOCK_STYLE = {
  flex: 'none',
  margin: '12px 20px 0',
  padding: '12px 16px',
  borderRadius: 16,
  background: 'color-mix(in srgb, var(--good) 10%, var(--panel))',
  border: '1px solid color-mix(in srgb, var(--good) 32%, var(--border))',
} as const;

/** `6:41`, or `1:02:03` past the hour. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = String(total % 60).padStart(2, '0');
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

/**
 * The bar the shell shows on every section while this tab is on a call: what
 * the call is, how long it has run, mute, Leave and the way back to the page
 * it lives on. Also carries the message when a call ends without the person
 * leaving it, since by then there is no call left to hang it on. Hidden on the
 * call's own page, which draws the same controls itself.
 */
export function CallDock({ current, go }: { current: string; go: (path: string) => void }) {
  const session = useCallSession();
  const call = session?.call ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [call]);

  if (!session) return null;
  if (!call) {
    return session.notice ? (
      <div style={DOCK_STYLE} className="flex items-center gap-3" role="alert">
        <span className="flex-1 text-sm text-bad">{session.notice}</span>
        <Button size="sm" onPress={session.dismissNotice}>
          Dismiss
        </Button>
      </div>
    ) : null;
  }
  // On the page the call lives on, the room's own call card (or the full
  // view) carries the controls: a second bar there would repeat them.
  if (current === call.path) return null;

  const self = session.stats?.participants.find((person) => person.self);
  const muted = self?.media ? !self.media.microphone : false;
  const { endpoint } = session;
  const waiting = session.lobby?.waiting.length ?? 0;

  return (
    <section style={DOCK_STYLE} className="flex flex-wrap items-center gap-3" aria-label="Call bar">
      <span className="flex-1 flex flex-col min-w-0">
        <span className="text-sm font-strong">{call.title}</span>
        <span className="text-xs text-muted" role="status">
          {session.joining
            ? 'Joining the call…'
            : `On the call · ${formatDuration(now - call.startedAt)}`}
        </span>
      </span>
      {waiting > 0 && (
        <Pill tone="warn" role="status">
          {session.lobby?.notice ?? `${waiting} waiting to join`}
        </Pill>
      )}
      {current !== call.path && (
        <Button size="sm" onPress={() => go(call.path)}>
          Back to the call
        </Button>
      )}
      <Button
        size="sm"
        aria-pressed={muted}
        disabled={!endpoint || session.joining}
        onPress={() => {
          endpoint?.setMicrophoneEnabled(muted).catch((error: unknown) => {
            console.error('Could not change the microphone:', error);
          });
        }}
      >
        {muted ? 'Unmute' : 'Mute'}
      </Button>
      <Button kind="danger" size="sm" onPress={session.leave}>
        <Icon name="phone-down" size={16} />
        <span>Leave</span>
      </Button>
    </section>
  );
}
